import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useStoreContext } from './StoreContext';

const API = import.meta.env.VITE_API_URL || 'https://api.dilzee.com';

interface CurrencyContextType {
  currency: string;
  setCurrency: (c: string) => void;
  convert: (paise: number) => string;
  convertNumber: (paise: number) => number;
  rates: Record<string, number>;
  loading: boolean;
  detectedCountry: string | null;
  availableCurrencies: { code: string; symbol: string; name: string }[];
}

const CURRENCIES = [
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AED', symbol: 'AED', name: 'UAE Dirham' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit' },
  { code: 'SAR', symbol: 'SAR', name: 'Saudi Riyal' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira' },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling' },
  { code: 'EGP', symbol: 'E£', name: 'Egyptian Pound' },
  { code: 'MAD', symbol: 'MAD', name: 'Moroccan Dirham' },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar' },
  { code: 'TWD', symbol: 'NT$', name: 'New Taiwan Dollar' },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
  { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah' },
  { code: 'THB', symbol: '฿', name: 'Thai Baht' },
  { code: 'VND', symbol: '₫', name: 'Vietnamese Dong' },
  { code: 'PHP', symbol: '₱', name: 'Philippine Peso' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'ARS', symbol: '$', name: 'Argentine Peso' },
  { code: 'CLP', symbol: '$', name: 'Chilean Peso' },
  { code: 'COP', symbol: '$', name: 'Colombian Peso' },
  { code: 'PEN', symbol: 'S/', name: 'Peruvian Sol' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'PLN', symbol: 'zł', name: 'Polish Zloty' },
  { code: 'TRY', symbol: '₺', name: 'Turkish Lira' },
  { code: 'RUB', symbol: '₽', name: 'Russian Ruble' },
];

const SYMBOL_MAP: Record<string, string> = Object.fromEntries(CURRENCIES.map(c => [c.code, c.symbol]));

const CurrencyContext = createContext<CurrencyContextType | null>(null);

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const { store } = useStoreContext();
  // Products are priced in the store's own currency (INR paise for Indian
  // stores, EUR cents for a French store, etc.). The exchange-rates API is
  // INR-base, so we derive the rate as target ÷ base.
  const baseCurrency = ((store as any)?.currency as string) || 'INR';
  const [currency, setCurrencyState] = useState<string>(() => {
    try { return localStorage.getItem('dilzee_currency') || ''; } catch { return ''; }
  });
  const [rates, setRates] = useState<Record<string, number>>({ INR: 1 });
  const [loading, setLoading] = useState(true);
  const [detectedCountry, setDetectedCountry] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API}/public/exchange-rates`)
      .then(r => r.json())
      .then((data: any) => {
        if (data.rates) setRates(data.rates);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetch(`${API}/public/detect-location`)
      .then(r => r.json())
      .then((data: any) => {
        setDetectedCountry(data.country);
        const hasManualChoice = (() => {
          try { return localStorage.getItem('dilzee_currency_manual') === '1'; } catch { return false; }
        })();
        if (!hasManualChoice && data.currency) {
          setCurrencyState(data.currency);
          try { localStorage.setItem('dilzee_currency', data.currency); } catch {}
        }
      })
      .catch(() => {
        if (!currency) setCurrencyState('INR');
      });
  }, []);

  const setCurrency = useCallback((c: string) => {
    setCurrencyState(c);
    try {
      localStorage.setItem('dilzee_currency', c);
      localStorage.setItem('dilzee_currency_manual', '1');
    } catch {}
  }, []);

  const convert = useCallback((paise: number): string => {
    const baseAmount = paise / 100;
    const baseRate = rates[baseCurrency] ?? (baseCurrency === 'INR' ? 1 : undefined);
    const targetRate = rates[currency] ?? (currency === 'INR' ? 1 : undefined);
    // rates are INR-base (value of 1 INR in each currency).
    // base → target = target/INR ÷ base/INR
    const rate = (baseRate != null && targetRate != null && baseRate > 0) ? targetRate / baseRate : 1;
    const converted = baseAmount * rate;
    const symbol = SYMBOL_MAP[currency] || currency;

    if (currency === 'JPY') {
      return `${symbol}${Math.round(converted).toLocaleString()}`;
    }
    if (currency === 'INR') {
      return `${symbol}${converted.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
    return `${symbol}${converted.toFixed(2)}`;
  }, [currency, rates, baseCurrency]);

  const convertNumber = useCallback((paise: number): number => {
    const baseAmount = paise / 100;
    const baseRate = rates[baseCurrency] ?? (baseCurrency === 'INR' ? 1 : undefined);
    const targetRate = rates[currency] ?? (currency === 'INR' ? 1 : undefined);
    const rate = (baseRate != null && targetRate != null && baseRate > 0) ? targetRate / baseRate : 1;
    return baseAmount * rate;
  }, [currency, rates, baseCurrency]);

  return (
    <CurrencyContext.Provider value={{ currency: currency || 'INR', setCurrency, convert, convertNumber, rates, loading, detectedCountry, availableCurrencies: CURRENCIES }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used inside CurrencyProvider');
  return ctx;
}
